import React from 'react';
import { BrowserRouter as Router, Link, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AppBar, Box, Button, Container, MenuItem, Select, Tab, Tabs, Toolbar, Typography } from '@mui/material';
import {
  AccountTree,
  AdminPanelSettings,
  AutoStories,
  Groups,
  History,
  LocalShipping,
  Logout,
  MonitorHeart,
  Palette,
  Workspaces,
} from '@mui/icons-material';
import { AuthProvider, useAuth } from './AuthContext';
import AuditDashboard from './components/AuditDashboard';
import DesignTemplateManagement from './components/DesignTemplateManagement';
import LoginPage from './components/LoginPage';
import OperationsStatus from './components/OperationsStatus';
import ProjectDashboard from './components/ProjectDashboard';
import ProjectWorkspace from './components/ProjectWorkspace';
import PublishingDashboard from './components/PublishingDashboard';
import UserManagement from './components/UserManagement';
import { LanguageProvider, useLanguage } from './LanguageContext';
import { languageOptions } from './components/projectConstants';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#24524f',
    },
    secondary: {
      main: '#8a4b38',
    },
    background: {
      default: '#f6f7f4',
      paper: '#ffffff',
    },
  },
  shape: {
    borderRadius: 6,
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 48,
          textTransform: 'none',
          fontWeight: 700,
        },
      },
    },
  },
});

const adminNavItems = [
  { labelKey: 'projects', path: '/admin/projects', icon: <AccountTree /> },
  { labelKey: 'publishing', path: '/admin/publishing', icon: <LocalShipping /> },
  { labelKey: 'usersAndAccess', path: '/admin/users', icon: <Groups /> },
  { labelKey: 'designTemplates', path: '/admin/design-templates', icon: <Palette /> },
  { labelKey: 'audit', path: '/admin/audit', icon: <History /> },
  { labelKey: 'operations', path: '/admin/operations', icon: <MonitorHeart /> },
];

const defaultAuthenticatedPath = (isAdmin?: boolean) => (isAdmin ? '/admin/projects' : '/projects');

function LoadingScreen() {
  const { t } = useLanguage();
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default' }}>
        <Typography color="text.secondary">{t('loadingWorkspace')}</Typography>
      </Box>
    </ThemeProvider>
  );
}

function CommonHeader() {
  const { direction, language, setLanguage, t } = useLanguage();
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const isAdminArea = location.pathname.startsWith('/admin');
  const workspaceSwitch = currentUser?.is_admin
    ? {
        path: isAdminArea ? '/projects' : '/admin/projects',
        label: isAdminArea ? t('projectPortal') : t('adminConsole'),
        icon: isAdminArea ? <Workspaces /> : <AdminPanelSettings />,
      }
    : null;

  return (
    <AppBar position="static" elevation={0}>
      <Toolbar
        sx={{
          minHeight: 64,
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr) auto minmax(0, 1fr)', md: '1fr auto 1fr' },
          gap: { xs: 1, md: 2 },
        }}
        dir={direction}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          {currentUser && (
            <Button
              color="inherit"
              startIcon={<Logout />}
              onClick={logout}
              sx={{ px: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}
            >
              {t('signOut')}
            </Button>
          )}
          {currentUser && (
            <Typography
              variant="body2"
              noWrap
              sx={{ display: { xs: 'none', lg: 'block' }, maxWidth: 240, opacity: 0.82 }}
            >
              {currentUser.display_name || currentUser.email}
            </Typography>
          )}
          <Select
            aria-label={t('language')}
            size="small"
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
            sx={{
              minWidth: { xs: 92, sm: 118 },
              color: 'primary.contrastText',
              '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.42)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.72)' },
              '.MuiSvgIcon-root': { color: 'primary.contrastText' },
            }}
          >
            {languageOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Button
          color="inherit"
          component={Link}
          to={currentUser ? defaultAuthenticatedPath(currentUser.is_admin) : '/login'}
          startIcon={<AutoStories />}
          sx={{
            px: { xs: 0.5, sm: 1 },
            fontSize: { xs: 18, sm: 20 },
            fontWeight: 800,
            justifySelf: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          Talegence
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
          {workspaceSwitch && (
            <Button
              color="inherit"
              component={Link}
              to={workspaceSwitch.path}
              startIcon={workspaceSwitch.icon}
              sx={{ whiteSpace: 'nowrap', px: { xs: 1, sm: 1.5 } }}
            >
              {workspaceSwitch.label}
            </Button>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}

function NavigationTabs({
  items,
  switchAction,
}: {
  items: Array<{ labelKey: string; path: string; icon: React.ReactElement }>;
  switchAction?: React.ReactNode;
}) {
  const location = useLocation();
  const { t } = useLanguage();
  const activePath = items.find((item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`))?.path || items[0].path;

  return (
    <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
      <Container maxWidth="xl" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Tabs value={activePath} variant="scrollable" scrollButtons="auto" aria-label={t('primaryNavigation')} sx={{ flex: 1 }}>
          {items.map((item) => (
            <Tab
              key={item.path}
              label={t(item.labelKey)}
              value={item.path}
              icon={item.icon}
              iconPosition="start"
              component={Link}
              to={item.path}
            />
          ))}
        </Tabs>
        {switchAction}
      </Container>
    </Box>
  );
}

function RequireAuth() {
  const { currentUser } = useAuth();
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
}

function RequireAdmin() {
  const { currentUser } = useAuth();
  if (!currentUser?.is_admin) {
    return <Navigate to="/projects" replace />;
  }
  return <Outlet />;
}

function ProjectLayout() {
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Outlet />
    </Container>
  );
}

function AdminLayout() {
  return (
    <>
      <NavigationTabs items={adminNavItems} />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Outlet />
      </Container>
    </>
  );
}

function AppRoutes() {
  const { currentUser } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={<Navigate to={currentUser ? defaultAuthenticatedPath(currentUser.is_admin) : '/login'} replace />}
      />
      <Route element={<RequireAuth />}>
        <Route element={<ProjectLayout />}>
          <Route path="/projects" element={<ProjectDashboard />} />
          <Route path="/projects/:projectId" element={<ProjectWorkspace />} />
        </Route>
        <Route element={<RequireAdmin />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<Navigate to="/admin/projects" replace />} />
            <Route path="/admin/projects" element={<ProjectDashboard scope="admin" />} />
            <Route path="/admin/publishing" element={<PublishingDashboard />} />
            <Route path="/admin/users" element={<UserManagement />} />
            <Route path="/admin/design-templates" element={<DesignTemplateManagement />} />
            <Route path="/admin/audit" element={<AuditDashboard />} />
            <Route path="/admin/operations" element={<OperationsStatus />} />
          </Route>
        </Route>
        <Route path="/publishing" element={<Navigate to="/admin/publishing" replace />} />
        <Route path="/users" element={<Navigate to="/admin/users" replace />} />
        <Route path="/audit" element={<Navigate to="/admin/audit" replace />} />
        <Route path="/operations" element={<Navigate to="/admin/operations" replace />} />
      </Route>
      <Route path="*" element={<Navigate to={currentUser ? defaultAuthenticatedPath(currentUser.is_admin) : '/login'} replace />} />
    </Routes>
  );
}

function AppShell() {
  const { direction } = useLanguage();
  const { loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          '&[dir="rtl"] .MuiFormControl-root, &[dir="rtl"] .MuiTextField-root, &[dir="rtl"] .MuiOutlinedInput-root': {
            direction: 'rtl',
          },
          '&[dir="rtl"] .MuiInputBase-input, &[dir="rtl"] textarea.MuiInputBase-input, &[dir="rtl"] .MuiSelect-select': {
            textAlign: 'right',
          },
          '&[dir="rtl"] .MuiInputBase-input::placeholder': {
            textAlign: 'right',
          },
          '&[dir="rtl"] .MuiInputLabel-root': {
            left: 'auto',
            right: 14,
            textAlign: 'right',
            transformOrigin: 'top right',
          },
          '&[dir="rtl"] .MuiInputLabel-root:not(.MuiInputLabel-shrink):not(.MuiInputLabel-sizeSmall)': {
            transform: 'translate(0, 16px) scale(1)',
          },
          '&[dir="rtl"] .MuiInputLabel-root.MuiInputLabel-sizeSmall:not(.MuiInputLabel-shrink)': {
            transform: 'translate(0, 9px) scale(1)',
          },
          '&[dir="rtl"] .MuiInputLabel-root.MuiInputLabel-shrink': {
            transform: 'translate(0, -9px) scale(0.75)',
          },
          '&[dir="rtl"] .MuiOutlinedInput-notchedOutline': {
            textAlign: 'right',
          },
          '&[dir="rtl"] .MuiSelect-select.MuiSelect-outlined': {
            paddingLeft: '32px',
            paddingRight: '14px',
          },
          '&[dir="rtl"] .MuiSelect-icon': {
            left: 7,
            right: 'auto',
          },
          '&[dir="rtl"] .MuiTableCell-root': {
            textAlign: 'right',
          },
          '&[dir="rtl"] .MuiTableCell-root .MuiTypography-root': {
            textAlign: 'right',
          },
          '&[dir="rtl"] .MuiTableCell-alignRight': {
            textAlign: 'right',
          },
          '& .MuiButton-root, & .MuiTab-root': {
            columnGap: 0.75,
          },
          '& .MuiButton-startIcon, & .MuiButton-endIcon, & .MuiTab-iconWrapper': {
            margin: 0,
          },
          '& .MuiListItemIcon-root': {
            marginInlineStart: 0,
            marginInlineEnd: '8px',
          },
          '&[dir="rtl"] .MuiListItemIcon-root': {
            marginInlineStart: '8px',
            marginInlineEnd: 0,
          },
          '& .MuiChip-icon': {
            marginInlineStart: '5px',
            marginInlineEnd: '-2px',
          },
          '& .MuiChip-deleteIcon': {
            marginInlineStart: '-2px',
            marginInlineEnd: '5px',
          },
        }}
        dir={direction}
      >
        <CommonHeader />
        <AppRoutes />
      </Box>
    </ThemeProvider>
  );
}

function AppWithRouter() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <AppShell />
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default AppWithRouter;
